import { io, Socket } from 'socket.io-client';

export interface AttendeeUser {
  userId: string | null;
  name: string;
  role: string;
  isModerator: boolean;
  isAuctioneer: boolean;
  isVerified: boolean;
  verificationTier: string | null;
  trustedBidder: boolean;
  canGrantMic: boolean;
}

export interface AuctionSocketCallbacks {
  userName?: string;
  onSettings?: (settings: Record<string, unknown>) => void;
  onAttendees?: (list: AttendeeUser[]) => void;
  onUserJoined?: (data: { user: AttendeeUser; totalAttendees: number }) => void;
  onUserLeft?: (data: { user: { userId: string | null; name: string }; totalAttendees: number }) => void;
  onBidsHistory?: (bids: Array<{ id: string; amount: number; userId: string; user?: { id: string; name: string }; createdAt: string }>) => void;
  onMessagesHistory?: (msgs: Array<{ id: string; userId: string | null; message: string; user?: { name: string } | null; createdAt: string }>) => void;
  onNewBid?: (bid: { id: string; userId: string; userName: string; amount: number; createdAt: string }) => void;
  onNewMessage?: (msg: { id: string; userId: string; userName: string; message: string; createdAt: string }) => void;
  onStageChanged?: (data: { stage: number; auctioneerName?: string; reason?: string; at: number }) => void;
  onAuctionExtended?: (data: { newEndAt: string }) => void;
  onOutbid?: (data: { newAmount: number; auctionId: string }) => void;
  onJoinDenied?: (data: { code: string; message: string }) => void;
  onSpeakState?: (data: { granted: string[]; pending: Array<{ userId: string; name: string; at: number }> }) => void;
  onSpeakRequest?: (data: { userId: string; name: string; at: number }) => void;
  onSpeakRequestCancelled?: (data: { userId: string }) => void;
  onSpeakRequestDenied?: (data: { userId: string }) => void;
  onMicGranted?: (data: { userId: string; by: string }) => void;
  onMicRevoked?: (data: { userId: string; by: string }) => void;
  onMicState?: (data: { userId: string; name: string; on: boolean }) => void;
  onAuctionEnded?: (data: { winner: { id: string; name: string } | null; finalAmount: number; noSale?: boolean; noSaleReason?: string | null }) => void;
  // 🛡️ تحكّم الجمهور (محرّج/أدمن)
  onUserMuted?: (data: { userId: string; by: string }) => void;
  onUserUnmuted?: (data: { userId: string; by: string }) => void;
  onUserKicked?: (data: { userId: string; by: string }) => void;
  onUserBanned?: (data: { userId: string; by: string }) => void;
  onKicked?: (data: { by: string; reason: string }) => void;
}

export interface AuctionSocketHandle {
  socket: Socket;
  emitStage: (stage: number) => void;
  toggleMic: (on: boolean) => void;
  requestSpeak: () => void;
  cancelSpeakRequest: () => void;
  grantMic: (targetUserId: string) => void;
  revokeMic: (targetUserId: string) => void;
  denySpeakRequest: (targetUserId: string) => void;
  // 🛡️ تحكّم الجمهور
  muteChat: (targetUserId: string) => void;
  unmuteChat: (targetUserId: string) => void;
  kickUser: (targetUserId: string) => void;
  banUser: (targetUserId: string) => void;
  sendChat: (message: string) => Promise<void>;
  placeBid: (amount: number) => Promise<void>;
  notSold: (reason: string) => void;
  leave: () => void;
  disconnect: () => void;
}

// 🌐 dev: localhost:3000 — prod: نفس origin الفرونت
const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.DEV
    ? 'http://localhost:3000'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

export function connectAuctionSocket(auctionId: string, callbacks: AuctionSocketCallbacks): AuctionSocketHandle {
  const socket = io(API_BASE, {
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });

  socket.on('connect', () => {
    socket.emit('auction:join', { auctionId, name: callbacks.userName });
  });

  socket.on('auction:settings',          (d) => callbacks.onSettings?.(d));
  socket.on('auction:attendees',         (d) => callbacks.onAttendees?.(d));
  socket.on('auction:user_joined',       (d) => callbacks.onUserJoined?.(d));
  socket.on('auction:user_left',         (d) => callbacks.onUserLeft?.(d));
  socket.on('auction:bids_history',      (d) => callbacks.onBidsHistory?.(d));
  socket.on('auction:messages_history',  (d) => callbacks.onMessagesHistory?.(d));
  socket.on('auction:new_bid',           (d) => callbacks.onNewBid?.(d));
  socket.on('auction:new_message',       (d) => callbacks.onNewMessage?.(d));
  socket.on('auction:stage_changed',     (d) => callbacks.onStageChanged?.(d));
  socket.on('auction:extended',          (d) => callbacks.onAuctionExtended?.(d));
  socket.on('auction:outbid',            (d) => callbacks.onOutbid?.(d));
  socket.on('auction:join_denied',       (d) => callbacks.onJoinDenied?.(d));
  socket.on('auction:speak_state',             (d) => callbacks.onSpeakState?.(d));
  socket.on('auction:speak_request',           (d) => callbacks.onSpeakRequest?.(d));
  socket.on('auction:speak_request_cancelled', (d) => callbacks.onSpeakRequestCancelled?.(d));
  socket.on('auction:speak_request_denied',    (d) => callbacks.onSpeakRequestDenied?.(d));
  socket.on('auction:mic_granted',             (d) => callbacks.onMicGranted?.(d));
  socket.on('auction:mic_revoked',             (d) => callbacks.onMicRevoked?.(d));
  socket.on('auction:mic_state',               (d) => callbacks.onMicState?.(d));
  socket.on('auction:ended',                   (d) => callbacks.onAuctionEnded?.(d));
  socket.on('auction:user_muted',              (d) => callbacks.onUserMuted?.(d));
  socket.on('auction:user_unmuted',            (d) => callbacks.onUserUnmuted?.(d));
  socket.on('auction:user_kicked',             (d) => callbacks.onUserKicked?.(d));
  socket.on('auction:user_banned',             (d) => callbacks.onUserBanned?.(d));
  socket.on('auction:kicked',                  (d) => callbacks.onKicked?.(d));

  return {
    socket,
    emitStage:           (stage) => socket.emit('auction:stage', { auctionId, stage }),
    toggleMic:           (on)    => socket.emit('auction:mic_toggle', { auctionId, on }),
    requestSpeak:        ()      => socket.emit('auction:request_speak', { auctionId }),
    cancelSpeakRequest:  ()      => socket.emit('auction:cancel_speak_request', { auctionId }),
    grantMic:            (targetUserId) => socket.emit('auction:grant_mic',   { auctionId, targetUserId }),
    revokeMic:           (targetUserId) => socket.emit('auction:revoke_mic',  { auctionId, targetUserId }),
    denySpeakRequest:    (targetUserId) => socket.emit('auction:deny_speak_request', { auctionId, targetUserId }),
    muteChat:            (targetUserId) => socket.emit('auction:mute_chat',   { auctionId, targetUserId }),
    unmuteChat:          (targetUserId) => socket.emit('auction:unmute_chat', { auctionId, targetUserId }),
    kickUser:            (targetUserId) => socket.emit('auction:kick',        { auctionId, targetUserId }),
    banUser:             (targetUserId) => socket.emit('auction:ban',         { auctionId, targetUserId }),
    notSold:             (reason: string) => socket.emit('auction:not_sold',  { auctionId, reason }),
    placeBid: async (amount) => {
      const res = await fetch(`${API_BASE}/api/auctions/${auctionId}/bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? 'فشل المزايدة');
    },
    sendChat: async (message) => {
      const res = await fetch(`${API_BASE}/api/auctions/${auctionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? 'فشل الإرسال');
    },
    leave: () => socket.emit('auction:leave', { auctionId }),
    disconnect: () => {
      socket.emit('auction:leave', { auctionId });
      socket.disconnect();
    },
  };
}
